import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not reset password");
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8"><Logo /></div>
        {!token ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="reset-no-token">
            This reset link is missing its token. Please request a new password reset.
          </div>
        ) : done ? (
          <div className="space-y-4" data-testid="reset-done">
            <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
              <CheckCircle2 className="h-5 w-5 shrink-0" /> Your password has been reset. You can now sign in with your new password.
            </div>
            <Button onClick={() => navigate("/login")} data-testid="reset-goto-login" className="w-full bg-kilted-green hover:bg-kilted-greenLight">Go to sign in</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            <div className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-kilted-gold" />
              <h2 className="font-heading text-2xl font-extrabold tracking-tight text-kilted-green">Choose a new password</h2>
            </div>
            <p className="text-sm text-muted-foreground">Enter a new password for your Kilted Courier account.</p>
            <div className="space-y-1.5"><Label htmlFor="np">New password</Label><Input id="np" type="password" minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="reset-password-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="cp">Confirm password</Label><Input id="cp" type="password" minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-confirm-input" /></div>
            <Button type="submit" disabled={loading} data-testid="reset-submit-button" className="w-full gap-2 bg-kilted-green hover:bg-kilted-greenLight">{loading && <Loader2 className="h-4 w-4 animate-spin" />} Reset password</Button>
          </form>
        )}
      </div>
    </div>
  );
}
